import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { CURRENCIES, DEFAULT_LATE_FEE_RULE, type Vocabulary } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { useModuleLink } from "@/lib/module-rail";
import { Alert, Button, Card, CardTitle, Field, Input, LinkButton, PageHeader, Select, Spinner, Textarea } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { PhoneInput } from "@/components/phone-input";
import { SearchSelect } from "@/components/search-select";
import { VocabularyEditor } from "@/components/vocabulary-editor";
import { ModuleSwitches } from "@/components/module-switches";
import { AgencyMark, IconAgencyMark, IconUpload } from "@/components/agency-mark";
import { WalkthroughVisibility } from "@/components/walkthrough";

/**
 * Agency settings.
 *
 * Two things here are load-bearing beyond their size. **Vocabulary**: an agency that says "shops"
 * and "occupants" sees those words on every screen, which is what stops the product feeling
 * foreign on day one. **Grace days and the late-fee rule**: they decide what "overdue" means,
 * and therefore what the whole dashboard shows.
 */

interface SettingsResponse {
  org: Record<string, string | number | boolean | null>;
  vocabulary: Vocabulary;
  vocabularyPresets: Array<{ name: string; description: string; vocabulary: Vocabulary }>;
  plan: { code: string; status: string; trialDaysLeft: number | null };
  usage: { units: { used: number; limit: number }; messages: { used: number; limit: number } };
  role: string;
}

export function SettingsPage() {
  const { can, refresh, modules } = useSession();
  /* Settings sits on both rails, and every quick link below is a screen both modules share. They
     keep the module they were pressed from — see `useModuleLink`. */
  const link = useModuleLink(modules);
  const [savedSection, setSavedSection] = useState<string | null>(null);
  const [currency, setCurrency] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResponse>("/settings"),
  });

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch("/settings", input),
    invalidates: [["settings"], ["me"], ["dashboard"]],
    onSuccess: async () => {
      await refresh();
      await refetch();
    },
  });

  const org = data?.org ?? {};

  useEffect(() => {
    if (data?.org?.currency) setCurrency(String(data.org.currency));
  }, [data?.org?.currency]);

  /**
   * The wording being edited, held locally until it is saved.
   *
   * Saving on every dropdown would send twelve requests to change five words, and each one would
   * re-render every screen behind this one as the labels changed under the user.
   */
  const [draftVocab, setDraftVocab] = useState<Vocabulary | null>(null);

  // Seed it the first time the settings arrive.
  useEffect(() => {
    if (data?.vocabulary && !draftVocab) setDraftVocab(data.vocabulary);
  }, [data, draftVocab]);

  /**
   * The agency's logo.
   *
   * Uploaded by hand rather than through the shared client, for the same reason a photograph is:
   * `api.post` sends JSON, and a file in a JSON body is base64 and a third larger on the
   * connection that is already the slow part.
   *
   * Every constraint the server enforces is repeated in the copy below, not in the code — a
   * second implementation of "is this a PNG" in the browser would only be a second thing to
   * disagree with the first. The file input's `accept` narrows the picker; the server decides.
   */
  const logoInput = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoProblem, setLogoProblem] = useState<string | null>(null);

  async function uploadLogo(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    setLogoProblem(null);
    setLogoBusy(true);

    const form = new FormData();
    form.append("logo", file);

    try {
      const response = await fetch("/api/v1/settings/logo", { method: "POST", body: form, credentials: "include" });
      if (response.ok) {
        /* Both, and in this order: the session carries `org.logoUrl` and it is what every
           document reads, so a settings page that refreshed only itself would show the new mark
           while the invoice behind it still showed the old one. */
        await refresh();
        await refetch();
      } else {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        setLogoProblem(payload?.error ?? "That file could not be uploaded.");
      }
    } catch {
      setLogoProblem("Could not reach the server. Check your connection and try again.");
    }

    setLogoBusy(false);
    // Cleared so choosing the same file twice still fires a change.
    if (logoInput.current) logoInput.current.value = "";
  }

  async function removeLogo() {
    setLogoProblem(null);
    setLogoBusy(true);
    try {
      await api.delete("/settings/logo");
      await refresh();
      await refetch();
    } catch {
      setLogoProblem("The logo could not be removed. Nothing has changed.");
    }
    setLogoBusy(false);
  }

  if (isLoading || !data || !draftVocab) return <Spinner />;
  const readOnly = !can("org.settings");
  const logoUrl = (org.logoUrl as string | null) ?? null;

  const submit = (section: string) => (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    const payload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values)) {
      payload[key] = value === "" ? null : value;
    }
    if (section === "agency") {
      payload.publicAddress =
        (event.currentTarget.elements.namedItem("publicAddress") as HTMLSelectElement)?.value ?? "full";
      payload.showBalanceOnReceipt =
        (event.currentTarget.elements.namedItem("showBalanceOnReceipt") as HTMLInputElement)?.checked ?? true;
    }
    if (section === "reminders") {
      payload.autoRemindersEnabled = (event.currentTarget.elements.namedItem("autoRemindersEnabled") as HTMLInputElement)?.checked;
    }
    if (section === "latefee") {
      payload.lateFeeRule = {
        enabled: (event.currentTarget.elements.namedItem("lateFeeEnabled") as HTMLInputElement)?.checked ?? false,
        basis: values.lateFeeBasis,
        value: values.lateFeeBasis === "percent_of_balance" ? Math.round(Number(values.lateFeeValue) * 100) : Math.round(Number(values.lateFeeValue) * 100),
        graceDays: Number(values.lateFeeGrace),
        frequency: values.lateFeeFrequency,
        periodDays: Number(values.lateFeePeriod),
        cap: values.lateFeeCap ? Math.round(Number(values.lateFeeCap) * 100) : 0,
      };
      for (const key of Object.keys(values)) if (key.startsWith("lateFee")) delete payload[key];
    }
    setSavedSection(section);
    save.mutate(payload);
  };

  const rule = { ...DEFAULT_LATE_FEE_RULE, ...((org.lateFeeRule as unknown as typeof DEFAULT_LATE_FEE_RULE) ?? {}) };

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Settings"
        // Team and Billing are their own items in the rail now. Repeating them here as buttons
        // gave the same destination two doors and made this page look like a hub it is not.
        description={"How this agency works: what things are called, what counts as late, when reminders may be sent, and what the agency looks like on a receipt."}
        actions={
          <div className="flex flex-wrap gap-2">
            {/*
              The two lists an agency keeps for itself, reachable but not resident.

              Both are added to from the form where the choice is being made — a beneficiary on a
              charge row, a payout method as a payout is marked paid — because the moment somebody
              needs one is the moment they are in the middle of something else. What is here is the
              way back to them afterwards, to correct a name typed in a hurry or retire an entry
              nobody uses. A person who never opens either screen still gets a working product,
              which is the test these two pages have to pass.
            */}
            <LinkButton to={link("/app/beneficiaries")} variant="secondary" need="party.view">Beneficiaries</LinkButton>
            <LinkButton to={link("/app/settings/lists")} variant="secondary" need="party.view">Your lists</LinkButton>
            <LinkButton to={link("/app/settings/api-keys")} variant="secondary" need="org.settings">API keys</LinkButton>
            <LinkButton to={link("/app/settings/audit")} variant="secondary" need="org.settings">Audit log</LinkButton>
          </div>
        }
      />

      {save.errorMessage ? <div className="mb-4"><Alert tone="danger">{save.errorMessage}</Alert></div> : null}
      {save.isSuccess ? <div className="mb-4"><Alert tone="success">Saved.</Alert></div> : null}

      <div className="space-y-5">
        <Card>
          <CardTitle hint="Appears on invoices, receipts and every reminder you send.">The agency</CardTitle>
          <form onSubmit={submit("agency")} className="space-y-4">
            <Field label="Name" htmlFor="name" required>
              <Input id="name" name="name" placeholder="Broadview Estates" defaultValue={String(org.name ?? "")} disabled={readOnly} required />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone" htmlFor="phone">
                <PhoneInput id="phone" name="phone" defaultValue={String(org.phone ?? "")} disabled={readOnly} />
              </Field>
              <Field label="Email" htmlFor="email">
                <Input id="email" name="email" type="email" placeholder="hello@agency.com" defaultValue={String(org.email ?? "")} disabled={readOnly} />
              </Field>
            </div>
            <Field label="Address" htmlFor="address">
              <Input id="address" name="address" placeholder="14 Bourdillon Road, Ikoyi" defaultValue={String(org.address ?? "")} disabled={readOnly} />
            </Field>

            {/* This is about the **properties you advertise**, not about the agency's own address
                above it — which is why it says so. The two sit together because somebody thinking
                about one is thinking about the other. */}
            <Field
              label="How much of an address to show on the public site"
              htmlFor="publicAddress"
              hint="Applies to every property you list. It does not change anything your staff or your clients see."
            >
              <Select id="publicAddress" name="publicAddress" defaultValue={String(org.publicAddress ?? "full")} disabled={readOnly}>
                <option value="full">The full address — 14 Bourdillon Road, Ikoyi, Lagos</option>
                <option value="area">The area only — Ikoyi, Lagos</option>
              </Select>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Currency" htmlFor="currency" hint="Changing this does not convert existing amounts.">
                <SearchSelect
                  id="currency"
                  name="currency"
                  value={currency}
                  onChange={(next) => setCurrency(next)}
                  disabled={readOnly}
                  required
                  options={Object.values(CURRENCIES).map((entry) => ({
                    value: entry.code,
                    label: `${entry.code} — ${entry.symbol}`,
                  }))}
                />
              </Field>
              <Field label="Timezone" htmlFor="timezone" hint="Decides what counts as today, and therefore what is overdue.">
                <Input id="timezone" name="timezone" placeholder="Region/City" defaultValue={String(org.timezone ?? "")} disabled={readOnly} />
              </Field>
            </div>
            <Field label="Receipt footer" htmlFor="receiptFooter" hint="Printed at the bottom of every receipt.">
              <Textarea id="receiptFooter" name="receiptFooter" rows={2} placeholder="Thank you. Please keep this receipt." defaultValue={String(org.receiptFooter ?? "")} disabled={readOnly} />
            </Field>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                name="showBalanceOnReceipt"
                defaultChecked={org.showBalanceOnReceipt !== false}
                disabled={readOnly}
                className="mt-0.5 size-4 rounded border-ink-300"
              />
              <span>
                Show what the client still owes on their receipt
                <span className="block text-xs text-ink-500">
                  On by default — a {data.vocabulary.tenant} who has part-paid needs to see the remainder. Switch it off if you hand
                  receipts to third parties who should not see the arrear.
                </span>
              </span>
            </label>
            {!readOnly ? <Button type="submit" disabled={save.isPending && savedSection === "agency"}>Save</Button> : null}
          </form>
        </Card>

        {/*
          The logo, immediately under the agency's details, because it is part of the same
          question: what a client sees at the top of the paperwork this agency sends.
        */}
        <Card>
          <CardTitle hint="Shown at the head of every invoice, receipt and statement you issue, including the ones printed and the ones opened from a payment link.">
            <span className="inline-flex items-center gap-2">
              <IconAgencyMark size={17} className="text-ink-400" />
              Logo
            </span>
          </CardTitle>

          {logoProblem ? (
            <div className="mb-3">
              <Alert tone="danger">{logoProblem}</Alert>
            </div>
          ) : null}

          <div className="flex flex-wrap items-start gap-5">
            {/* The same treatment the documents use, so what is approved here is what is issued. */}
            <div
              className="grid min-h-24 w-56 place-items-center rounded-[var(--radius-surface)] border border-ink-200 bg-white p-4"
              data-testid="logo-preview"
            >
              {logoUrl ? (
                <AgencyMark name={String(org.name ?? "")} logoUrl={logoUrl} />
              ) : (
                <p className="text-center text-xs text-ink-500">
                  No logo has been uploaded. Documents carry the agency name on its own.
                </p>
              )}
            </div>

            <div className="min-w-56 flex-1">
              {!readOnly ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={logoBusy}
                    onClick={() => logoInput.current?.click()}
                  >
                    <IconUpload size={15} />
                    {logoBusy ? "Uploading…" : logoUrl ? "Replace logo" : "Upload a logo"}
                  </Button>
                  {logoUrl ? (
                    <Button type="button" variant="ghost" size="sm" disabled={logoBusy} onClick={removeLogo}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              ) : null}

              <p className="mt-3 text-xs text-ink-500">
                PNG, JPEG or WebP, up to 2MB. A PNG with a transparent background reproduces best on a printed
                document. A square or landscape mark works better than a tall one, which is scaled down to fit the
                height of the letterhead.
              </p>
              {/* Said plainly, so a refusal at the upload reads as a policy and not a defect. */}
              <p className="mt-2 text-xs text-ink-500">
                SVG files are not accepted. An SVG can carry executable instructions, and these documents are opened by
                your clients.
              </p>
            </div>
          </div>

          <input
            ref={logoInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(event) => uploadLogo(event.target.files)}
          />
        </Card>

        {/* Above the bank details, because it decides what half the rest of the application is
            for — and somebody who has just been sold the developer module comes here first. */}
        <ModuleSwitches readOnly={readOnly} />

        <Card>
          <CardTitle hint="What counts as late, and how far ahead “due soon” looks on the dashboard.">Money rules</CardTitle>
          <form onSubmit={submit("money")} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Grace days" htmlFor="graceDays" hint="Days after the due date before an invoice is overdue.">
                <Input id="graceDays" name="graceDays" inputMode="numeric" placeholder="0" defaultValue={String(org.graceDays ?? 0)} disabled={readOnly} />
              </Field>
              <Field label="“Due soon” window" htmlFor="dueSoonDays" hint="Days ahead the amber tile looks.">
                <Input id="dueSoonDays" name="dueSoonDays" inputMode="numeric" placeholder="30" defaultValue={String(org.dueSoonDays ?? 30)} disabled={readOnly} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Invoice prefix" htmlFor="invoicePrefix">
                <Input id="invoicePrefix" name="invoicePrefix" placeholder="INV" defaultValue={String(org.invoicePrefix ?? "INV")} disabled={readOnly} />
              </Field>
              <Field label="Receipt prefix" htmlFor="receiptPrefix">
                <Input id="receiptPrefix" name="receiptPrefix" placeholder="RCT" defaultValue={String(org.receiptPrefix ?? "RCT")} disabled={readOnly} />
              </Field>
            </div>
            {!readOnly ? <Button type="submit">Save</Button> : null}
          </form>
        </Card>

        <Card>
          <CardTitle hint="Off by default: a fee nobody agreed to is a fee nobody pays.">Late payment fee</CardTitle>
          <form onSubmit={submit("latefee")} className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="lateFeeEnabled" defaultChecked={rule.enabled} disabled={readOnly} className="size-4 rounded border-ink-300" />
              Charge a late fee automatically
            </label>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Basis" htmlFor="lateFeeBasis">
                <Select id="lateFeeBasis" name="lateFeeBasis" defaultValue={rule.basis} disabled={readOnly}>
                  <option value="percent_of_balance">Percentage of the balance</option>
                  <option value="fixed">A fixed amount</option>
                </Select>
              </Field>
              <Field label="Amount" htmlFor="lateFeeValue" hint="Percent, or money.">
                <Input id="lateFeeValue" name="lateFeeValue" inputMode="decimal" placeholder="5" defaultValue={String(rule.value / 100)} disabled={readOnly} />
              </Field>
              <Field label="After (days)" htmlFor="lateFeeGrace">
                <Input id="lateFeeGrace" name="lateFeeGrace" inputMode="numeric" placeholder="0" defaultValue={String(rule.graceDays)} disabled={readOnly} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="How often" htmlFor="lateFeeFrequency">
                <Select id="lateFeeFrequency" name="lateFeeFrequency" defaultValue={rule.frequency} disabled={readOnly}>
                  <option value="once">Once</option>
                  <option value="per_period">Every period</option>
                </Select>
              </Field>
              <Field label="Period (days)" htmlFor="lateFeePeriod">
                <Input id="lateFeePeriod" name="lateFeePeriod" inputMode="numeric" placeholder="30" defaultValue={String(rule.periodDays)} disabled={readOnly} />
              </Field>
              <Field label="Cap" htmlFor="lateFeeCap" hint="0 for no cap.">
                <MoneyInput id="lateFeeCap" name="lateFeeCap" currency={String(org.currency ?? "NGN")} defaultValue={String(rule.cap / 100)} disabled={readOnly} />
              </Field>
            </div>
            {!readOnly ? <Button type="submit">Save</Button> : null}
          </form>
        </Card>

        <Card>
          <CardTitle hint="Automation never sends outside these hours. Sending by hand always works.">Sending window</CardTitle>
          <form onSubmit={submit("reminders")} className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="autoRemindersEnabled"
                defaultChecked={Boolean(org.autoRemindersEnabled)}
                disabled={readOnly}
                className="size-4 rounded border-ink-300"
              />
              Send reminders automatically
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="From" htmlFor="sendFromHour">
                <Input id="sendFromHour" name="sendFromHour" inputMode="numeric" placeholder="8" defaultValue={String(org.sendFromHour ?? 8)} disabled={readOnly} />
              </Field>
              <Field label="Until" htmlFor="sendToHour">
                <Input id="sendToHour" name="sendToHour" inputMode="numeric" placeholder="20" defaultValue={String(org.sendToHour ?? 20)} disabled={readOnly} />
              </Field>
            </div>
            {!readOnly ? <Button type="submit">Save</Button> : null}
          </form>
        </Card>

        <Card>
          <CardTitle hint="Choose what each thing is called. Every screen follows.">Vocabulary</CardTitle>
          <VocabularyEditor
            value={draftVocab}
            onChange={setDraftVocab}
            readOnly={readOnly}
            saving={save.isPending}
            onSave={() => {
              setSavedSection("vocab");
              save.mutate({ vocabulary: draftVocab });
            }}
          />
        </Card>

        <Card>
          <CardTitle hint="The panel of first steps on the dashboard and its own screen. Turning it off does not remove the screen — it stays under Getting started.">
            Getting started
          </CardTitle>
          <div className="mt-2">
            <WalkthroughVisibility />
          </div>
          <div className="mt-4">
            <LinkButton to={link("/app/getting-started")} variant="secondary">Open the walkthrough</LinkButton>
          </div>
        </Card>

      </div>
    </div>
  );
}
